import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import srOnly from 'styles/tools/sr-only'

import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'

export type TripChatMobileTabId = 'chat' | 'list' | 'map'

export interface TripChatMobileTabItem {
  id: TripChatMobileTabId
  label: string
}

interface TripChatMobileTabsProps {
  activeTab: TripChatMobileTabId
  ariaLabel: string
  tabs: ReadonlyArray<TripChatMobileTabItem>
  onTabChange: (tabId: TripChatMobileTabId) => void
  /** Marks the Chat tab when a turn finished while the traveller was on another tab. */
  hasUnreadChat?: boolean
  inlineOnWideScreens?: boolean
}

const SegmentedOptionsRegion = styled.div<{ $inlineOnWideScreens: boolean }>`
  display: flex;
  flex-shrink: 0;
  min-height: ${rem(56)};
  align-items: center;
  padding: ${rem(8)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  > * {
    width: 100%;
  }

  ${props => props.$inlineOnWideScreens && css`
    @media (min-width: ${rem(350)}) {
      min-height: ${rem(60)};
      padding-inline: ${rem(4)};
    }
  `}
`

const UNREAD_DOT_SIZE = 8
const UNREAD_DOT_GAP = 2

// SegmentedOption's title sits inside an overflow:hidden grid, so the dot reserves space
// with padding rather than hanging off the label's edge, which would clip it.
const TabLabel = styled.span`
  position: relative;
  display: inline-block;

  &.has-unread {
    padding-inline-end: ${rem(UNREAD_DOT_SIZE + UNREAD_DOT_GAP)};
  }
`

const UnreadDot = styled.span`
  position: absolute;
  top: 0;
  inset-inline-end: 0;
  width: ${rem(UNREAD_DOT_SIZE)};
  height: ${rem(UNREAD_DOT_SIZE)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.messaging.urgency.normalBackground};
`

const HiddenLabel = styled.span`
  ${srOnly}
`

interface TripChatMobileTabOptionProps {
  activeTab: TripChatMobileTabId
  tab: TripChatMobileTabItem
  showUnreadDot: boolean
  unreadLabel: string
}

function TripChatMobileTabOption({ activeTab, tab, showUnreadDot, unreadLabel }: TripChatMobileTabOptionProps) {
  return (
    <SegmentedOption
      value={tab.id}
      title={
        <TabLabel className={clsx({ 'has-unread': showUnreadDot })}>
          {tab.label}
          {showUnreadDot && <>
            <UnreadDot aria-hidden="true" />
            <HiddenLabel>{unreadLabel}</HiddenLabel>
          </>}
        </TabLabel>
      }
      checked={activeTab === tab.id}
    />
  )
}

function TripChatMobileTabs({
  activeTab,
  ariaLabel,
  tabs,
  onTabChange,
  hasUnreadChat = false,
  inlineOnWideScreens = false,
}: TripChatMobileTabsProps) {
  const intl = useIntl()
  const unreadLabel = intl.formatMessage({
    id: 'm_0UlYDuMk1iIRJFNvI-',
    defaultMessage: 'New messages',
    description: 'Screen reader label on the mobile Chat tab when the assistant has replied while the traveller was viewing another tab',
  })

  const options = useMemo(() => tabs.map(tab => (
    <TripChatMobileTabOption
      key={tab.id}
      activeTab={activeTab}
      tab={tab}
      showUnreadDot={tab.id === 'chat' && hasUnreadChat}
      unreadLabel={unreadLabel}
    />
  )), [activeTab, hasUnreadChat, tabs, unreadLabel])

  return (
    <SegmentedOptionsRegion $inlineOnWideScreens={inlineOnWideScreens} role="group" aria-label={ariaLabel}>
      <SegmentedOptionsGroup<TripChatMobileTabId>
        inputsName="trip-chat-mobile-view"
        distribution="filled"
        onValueChange={onTabChange}
      >
        {options}
      </SegmentedOptionsGroup>
    </SegmentedOptionsRegion>
  )
}

export default TripChatMobileTabs
